import { Button, ErrorMessage, Skeleton } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  StorageUsageBar,
  storageUsageColor,
} from '@affine/core/components/storage-usage-bar';
import { usePrivateFolderStorage } from '@affine/core/modules/organize';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useCallback, useEffect } from 'react';

import type { SettingState } from '../../types';
import * as styles from './style.css';

export const QuotaBar = ({
  label,
  usedText,
  limitText,
  percent,
  color,
}: {
  label: string;
  usedText: string;
  limitText: string;
  percent: number | null;
  color: string | null;
}) => {
  return (
    <StorageUsageBar
      className={styles.storageQuotaBar}
      label={label}
      usedText={usedText}
      limitText={limitText}
      percent={percent}
      color={color}
    />
  );
};

const HIGH_USAGE_THRESHOLD = 80;
const UNKNOWN_STORAGE_VALUE = '...';
const formatStorageBytes = (value: number) => bytes.format(value) ?? '0 B';

export const WorkspaceQuotaPanel = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const quotaEntity = useService(WorkspaceQuotaService).quota;

  const isLoading = useLiveData(quotaEntity.isRevalidating$);
  const loadError = useLiveData(quotaEntity.error$);

  // Storage
  const usedFormatted = useLiveData(quotaEntity.usedFormatted$);
  const maxFormatted = useLiveData(quotaEntity.maxFormatted$);
  const storagePercent = useLiveData(quotaEntity.percent$);
  const storageColor = useLiveData(quotaEntity.color$);
  const privateStorage = usePrivateFolderStorage();

  useEffect(() => {
    if (backendAvailable) {
      quotaEntity.revalidate();
    }
  }, [backendAvailable, quotaEntity]);

  useEffect(() => {
    if (loadError && isBackendUnavailableError(loadError)) {
      markBackendUnavailable(loadError);
    }
  }, [loadError]);

  const handleUpgrade = useCallback(() => {
    onChangeSettingState?.({ activeTab: 'workspace:plans' });
  }, [onChangeSettingState]);

  const hasStorageData =
    storagePercent !== null && !!usedFormatted && !!maxFormatted;
  const backendUnavailableError =
    loadError !== null && isBackendUnavailableError(loadError);
  const showOfflineNotice = !backendAvailable || backendUnavailableError;
  const renderUnavailableQuota = () => (
    <SettingRow
      name={t['com.streamlain.workspace.storage']()}
      desc=""
      spreadCol={false}
    >
      <QuotaBar
        label={t['com.streamlain.storage.used.hint']()}
        usedText={UNKNOWN_STORAGE_VALUE}
        limitText={UNKNOWN_STORAGE_VALUE}
        percent={0}
        color={cssVarV2('text/tertiary')}
      />
    </SettingRow>
  );

  if (isLoading && !hasStorageData) {
    if (showOfflineNotice) {
      return renderUnavailableQuota();
    }
    if (loadError) {
      return <ErrorMessage>Load error</ErrorMessage>;
    }
    return <Skeleton height={26} />;
  }

  if (!hasStorageData && showOfflineNotice) {
    return renderUnavailableQuota();
  }
  if (!hasStorageData && loadError) {
    return <ErrorMessage>Load error</ErrorMessage>;
  }

  const showUpgradeHint =
    storagePercent !== null && storagePercent >= HIGH_USAGE_THRESHOLD;
  const privateLimit = privateStorage?.limitBytes ?? null;
  const privatePercent =
    privateStorage && privateLimit !== null && privateLimit > 0
      ? Math.min(100, (privateStorage.usedBytes / privateLimit) * 100)
      : privateStorage
        ? 0
        : null;

  return (
    <SettingRow
      name={t['com.streamlain.workspace.storage']()}
      desc=""
      spreadCol={false}
    >
      <QuotaBar
        label={t['com.streamlain.storage.used.hint']()}
        usedText={usedFormatted ?? '...'}
        limitText={maxFormatted ?? '...'}
        percent={storagePercent}
        color={storageColor}
      />
      {privateStorage && (
        <QuotaBar
          label={t['com.streamlain.settings.account.privateFolder.storage']()}
          usedText={formatStorageBytes(privateStorage.usedBytes)}
          limitText={
            privateLimit === null
              ? t['com.streamlain.storage.unlimited']()
              : formatStorageBytes(privateLimit)
          }
          percent={privatePercent}
          color={storageUsageColor(privatePercent)}
        />
      )}
      {showUpgradeHint && onChangeSettingState && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
            fontSize: 13,
            color: cssVarV2('text/secondary'),
          }}
        >
          <span>{t['com.streamlain.payment.storage-upgrade-hint']()}</span>
          <Button variant="primary" onClick={handleUpgrade}>
            {t['com.streamlain.payment.upgrade-plan']()}
          </Button>
        </div>
      )}
    </SettingRow>
  );
};
